<template>
  <div class="tulum-beach-clubs-container">
    <!-- 1. Hero Section -->
    <header class="hero-section" :style="{ backgroundImage: `url(${heroImage})` }">
      <div class="hero-overlay">
        <h1 class="hero-headline">The Ultimate Tulum Beach Club Guide</h1>
        <p class="hero-subheadline">Your expert guide to Tulum's most exclusive day clubs, beach parties, and seaside dining experiences.</p>
      </div>
    </header>

    <div class="page-content">
      <!-- Featured Partner Section -->
      <section class="featured-partner-section">
        <h2>Our Top Pick: Taboo Tulum</h2>
        <div class="featured-card">
          <ResponsiveImage :image="featuredPartnerImage" alt="Luxury experience at Taboo Tulum" class="featured-image"/>
          <div class="featured-content">
            <h3>Experience Unrivaled Energy & Luxury</h3>
            <p>Taboo Tulum sets the standard for a high-energy, luxurious beach club experience. With world-class DJs, a stunning beachfront location, and impeccable service, it's the definitive destination for a day party that transforms into a legendary night.</p>
            <div class="cta-buttons">
              <a href="#" class="cta-btn primary">Reserve a Table</a>
              <a href="#" class="cta-btn secondary">Book via WhatsApp</a>
            </div>
          </div>
        </div>
      </section>

      <hr class="section-divider">

      <!-- Interactive Directory -->
      <section class="directory-section">
        <h2>Find Your Perfect Beach Club</h2>
        <!-- Filtering Controls -->
        <div class="filters-container">
          <div class="filter-group">
            <label>Filter by Vibe:</label>
            <div class="filter-pills">
              <button v-for="vibe in filterOptions.vibes" :key="vibe" @click="toggleFilter('vibe', vibe)" :class="{ active: filters.vibe.includes(vibe) }">{{ vibe }}</button>
            </div>
          </div>
          <div class="filter-group">
            <label>Filter by Price:</label>
            <div class="filter-pills">
              <button v-for="price in filterOptions.prices" :key="price" @click="toggleFilter('price', price)" :class="{ active: filters.price.includes(price) }">{{ price }}</button>
            </div>
          </div>
        </div>

        <!-- Club Cards Grid -->
        <div class="clubs-grid">
          <div v-for="club in filteredClubs" :key="club.name" class="club-card" @click="openClubModal(club)">
            <ResponsiveImage :image="club.image" :alt="club.name" class="club-image"/>
            <div class="club-card-overlay">
              <div class="club-card-info">
                <h3>{{ club.name }}</h3>
                <span class="vibe-check">{{ club.vibe }}</span>
              </div>
              <div class="club-card-features">
                <span class="price-indicator">{{ club.price }}</span>
                <div class="feature-icons">
                  <span v-if="club.features.includes('Live DJ')" title="Live DJ">🎵</span>
                  <span v-if="club.features.includes('Sunset Views')" title="Sunset Views">🌅</span>
                  <span v-if="club.features.includes('Pool')" title="Pool">🏊</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr class="section-divider">

      <!-- Curated Content Module -->
      <section class="curated-module">
        <h2>The Vibe Guide: Match the Club to Your Mood</h2>
        <p>Whether you're looking for a high-energy party or a chic spot to relax, our curated guide helps you find the perfect match for your desired experience.</p>
        <!-- Add more curated content here -->
      </section>

    </div>

    <!-- Dynamic Club Profile Modal -->
    <transition name="fade">
      <div v-if="selectedClub" class="modal-overlay" @click="closeClubModal">
        <div class="modal-content" @click.stop>
          <button class="close-button" @click="closeClubModal">&times;</button>
          <div class="modal-header" :style="{ backgroundImage: `url(${selectedClub.image})`}">
            <div class="modal-header-overlay">
              <h2>{{ selectedClub.name }}</h2>
              <p class="vibe-check">{{ selectedClub.vibe }}</p>
            </div>
          </div>
          <div class="modal-body">
            <p>{{ selectedClub.description }}</p>
            <div class="modal-details">
              <div class="detail-item"><strong>Address:</strong> {{ selectedClub.address }}</div>
              <div class="detail-item"><strong>Hours:</strong> {{ selectedClub.hours }}</div>
              <div class="detail-item"><strong>Dress Code:</strong> {{ selectedClub.dressCode }}</div>
              <div class="detail-item"><strong>Minimum Spend:</strong> {{ selectedClub.minSpend }}</div>
            </div>
            <div class="cta-buttons modal-cta">
              <a href="#" class="cta-btn primary">Reserve a Table</a>
              <a href="#" class="cta-btn secondary">Book via WhatsApp</a>
            </div>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
// Assuming ResponsiveImage is globally registered or imported
import ResponsiveImage from '@/components/HeadImage/ResponsiveImage';

export default {
  name: 'TulumBeachClubs',
  components: { ResponsiveImage },
  metaInfo() {
    return {
      title: 'The Ultimate Tulum Beach Club Guide | 2025 Reservations & Prices',
      link: [{ rel: 'canonical', href: 'https://www.tulum.party/tulum-beach-clubs' }],
      meta: [
        { name: 'description', content: 'Discover the best beach clubs in Tulum for 2025. Your expert guide to day clubs, beach parties, prices, and VIP reservations at venues like Taboo, Papaya Playa, and more.' },
        { name: 'keywords', content: 'best tulum beach clubs, tulum beach club, tulum day club, tulum beach party, Taboo Tulum, tulum beach club prices, tulum beach club minimum spend' },
        { property: 'og:title', content: 'The Ultimate Tulum Beach Club Guide | 2025' },
        { property: 'og:description', content: 'Your expert guide to Tulum\'s best beach clubs. Find prices, make reservations, and plan your perfect day party.' },
        { property: 'og:image', content: this.heroImage },
        { property: 'og:url', content: 'https://www.tulum.party/tulum-beach-clubs' }
      ]
    };
  },
  data() {
    return {
      heroImage: 'https://placehold.co/1920x1080/1a1a1c/00c6ff?text=Tulum+Beach+Club+Paradise',
      featuredPartnerImage: 'https://placehold.co/1200x600/1a1a1c/d4af37?text=Taboo+Tulum+Experience',
      filters: {
        vibe: [],
        price: []
      },
      selectedClub: null,
      clubs: [
        { name: 'Taboo Tulum', vibe: 'High-Energy Party', price: '$$$$', features: ['Live DJ', 'Sunset Views'], image: 'https://placehold.co/600x400/1a1a1c/d4af37?text=Taboo', description: 'The definitive destination for a high-energy, luxurious day party with world-class music and a vibrant atmosphere.', address: 'Carretera Tulum-Boca Paila Km 7', hours: '11:00 AM - 7:00 PM', dressCode: 'Chic Beach Attire', minSpend: 'Varies by season, inquire for details.' },
        { name: 'Papaya Playa Project', vibe: 'Bohemian Chill', price: '$$$', features: ['Live DJ', 'Sunset Views'], image: 'https://placehold.co/600x400/1a1a1c/73854a?text=Papaya+Playa', description: 'A Tulum institution known for its rustic-chic design, spiritual vibe, and legendary full moon parties.', address: 'Carretera Tulum-Boca Paila Km 4.5', hours: '10:00 AM - 6:00 PM', dressCode: 'Boho Chic', minSpend: 'Varies' },
        { name: 'Bagatelle Tulum', vibe: 'Luxe & Exclusive', price: '$$$$', features: ['Live DJ', 'Pool', 'Restaurant'], image: 'https://placehold.co/600x400/1a1a1c/ff6f61?text=Bagatelle', description: 'A taste of the French Riviera in Tulum. Expect champagne, fine dining, and a glamorous party that builds throughout the day.', address: 'Carretera Tulum-Boca Paila Km 8', hours: '11:00 AM - 7:00 PM', dressCode: 'Smart Elegant', minSpend: 'High, inquire for details.' },
        // Add more clubs here
      ]
    };
  },
  computed: {
    filterOptions() {
      const vibes = new Set(this.clubs.map(c => c.vibe));
      const prices = new Set(this.clubs.map(c => c.price));
      return {
        vibes: Array.from(vibes),
        prices: Array.from(prices).sort((a,b) => a.length - b.length)
      }
    },
    filteredClubs() {
      return this.clubs.filter(club => {
        const vibeMatch = this.filters.vibe.length === 0 || this.filters.vibe.includes(club.vibe);
        const priceMatch = this.filters.price.length === 0 || this.filters.price.includes(club.price);
        return vibeMatch && priceMatch;
      });
    }
  },
  methods: {
    toggleFilter(type, value) {
      const filterArray = this.filters[type];
      const index = filterArray.indexOf(value);
      if (index > -1) {
        filterArray.splice(index, 1);
      } else {
        filterArray.push(value);
      }
    },
    openClubModal(club) {
      this.selectedClub = club;
    },
    closeClubModal() {
      this.selectedClub = null;
    }
  }
};
</script>

<style scoped>
.tulum-beach-clubs-container {
  background-color: #1a1a1c;
  color: #f0f0f0;
  font-family: Arial, sans-serif;
}
.hero-section {
  height: 80vh;
  min-height: 600px;
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hero-overlay {
  background: linear-gradient(to top, rgba(26, 26, 28, 0.9) 10%, rgba(26, 26, 28, 0.3) 100%);
  text-align: center;
  padding: 2rem;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.hero-headline {
  font-family: 'Oswald', sans-serif;
  font-size: 4.5rem;
  font-weight: 900;
  color: #fff;
  text-shadow: 2px 2px 10px rgba(0, 0, 0, 0.8);
  max-width: 1000px;
  line-height: 1.1;
  margin: 0;
}
.hero-subheadline {
  font-size: 1.25rem;
  max-width: 700px;
  line-height: 1.6;
  color: #ccc;
  margin-top: 1rem;
}
.page-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 1.5rem;
}
section {
  margin-bottom: 4rem;
}
section h2 {
  text-align: center;
  font-size: 2.8rem;
  font-weight: 700;
  color: #d4af37; /* Gold accent */
  margin-bottom: 2.5rem;
}
.featured-partner-section .featured-card {
  display: flex;
  gap: 2rem;
  background-color: #252528;
  border-radius: 12px;
  overflow: hidden;
}
.featured-image {
  width: 50%;
  object-fit: cover;
}
.featured-content {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.featured-content h3 { font-size: 2rem; margin: 0 0 1rem 0; }
.featured-content p { font-size: 1.1rem; line-height: 1.7; color: #ccc; margin-bottom: 2rem; }

.cta-buttons { display: flex; gap: 1rem; flex-wrap: wrap; }
.cta-btn { padding: 0.8rem 1.8rem; border-radius: 50px; text-decoration: none; font-weight: 700; font-size: 1rem; transition: all 0.3s ease; }
.cta-btn.primary { background-color: #d4af37; color: #1a1a1c; }
.cta-btn.primary:hover { background-color: #fff; }
.cta-btn.secondary { background-color: transparent; color: #d4af37; border: 2px solid #d4af37; }
.cta-btn.secondary:hover { background-color: #d4af37; color: #1a1a1c; }

.section-divider {
  border: 0;
  height: 1px;
  background-image: linear-gradient(to right, rgba(212, 175, 55, 0), rgba(212, 175, 55, 0.5), rgba(212, 175, 55, 0));
  margin: 4rem 0;
}

.filters-container { background-color: #252528; padding: 1.5rem; border-radius: 12px; margin-bottom: 2rem; }
.filter-group { margin-bottom: 1rem; }
.filter-group label { font-weight: bold; margin-right: 1rem; }
.filter-pills { display: flex; gap: 0.75rem; flex-wrap: wrap; }
.filter-pills button { background-color: #3a3a3c; border: 1px solid #555; color: #f0f0f0; padding: 0.5rem 1rem; border-radius: 50px; cursor: pointer; transition: all 0.2s ease; }
.filter-pills button.active { background-color: #d4af37; color: #1a1a1c; border-color: #d4af37; font-weight: bold; }

.clubs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}
.club-card {
  background-color: #2b2b2d;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(0,0,0,0.4);
}
.club-image { width: 100%; height: 400px; object-fit: cover; display: block; transition: transform 0.4s ease; }
.club-card:hover .club-image { transform: scale(1.05); }
.club-card-overlay { position: absolute; bottom: 0; left: 0; width: 100%; padding: 1.5rem; background: linear-gradient(to top, rgba(0,0,0,0.9), transparent); }
.club-card-info h3 { font-size: 1.8rem; margin: 0 0 0.25rem 0; color: #fff; }
.club-card-info .vibe-check { font-style: italic; color: #ccc; }
.club-card-features { display: flex; justify-content: space-between; align-items: center; margin-top: 1rem; }
.price-indicator { font-size: 1.2rem; font-weight: bold; color: #d4af37; }
.feature-icons { font-size: 1.5rem; }

.modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0,0,0,0.85); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-content { background-color: #2b2b2d; border-radius: 12px; max-width: 800px; width: 90%; position: relative; max-height: 90vh; overflow-y: auto; }
.close-button { position: absolute; top: 15px; right: 20px; background: none; border: none; color: #fff; font-size: 2.5rem; cursor: pointer; z-index: 10; }
.modal-header { height: 300px; background-size: cover; background-position: center; position: relative; }
.modal-header-overlay { background: linear-gradient(to top, rgba(43,43,45,1) 5%, transparent 100%); position: absolute; bottom: 0; left: 0; width: 100%; padding: 1.5rem; }
.modal-header-overlay h2 { font-size: 2.5rem; color: #fff; margin: 0; }
.modal-body { padding: 2rem; }
.modal-details { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-top: 1.5rem; border-top: 1px solid #444; padding-top: 1.5rem; }
.modal-cta { margin-top: 2rem; }

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

@media (max-width: 768px) {
  .featured-partner-section .featured-card { flex-direction: column; }
  .featured-image { width: 100%; height: 250px; }
}
</style>
